import { useEffect, useState } from 'react';
import { collection, addDoc, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

const DEFAULT_PRIZE_LIST = [
  { emoji: '👑', label: 'Winner', amount: '' },
  { emoji: '🥈', label: '2nd Position', amount: '' },
  { emoji: '🥉', label: '3rd Position', amount: '' },
  { emoji: '🏅', label: '4th Position', amount: '' },
  { emoji: '🏅', label: '5th Position', amount: '' },
];

const EMPTY_FORM = {
  title: '', mode: 'BR', entryFee: '', prize: '',
  perKill: '', totalSlots: '', matchTime: '', rules: '',
  map: 'Bermuda', type: 'Solo', device: 'Android',
  status: 'open',
  prizeList: DEFAULT_PRIZE_LIST,
};

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showRoomModal, setShowRoomModal] = useState(null);
  const [roomForm, setRoomForm] = useState({});
  const [showDetails, setShowDetails] = useState(null);
  const [detailParticipants, setDetailParticipants] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'matches'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMatches(data);
    });
    return () => unsub();
  }, []);

  const handleSubmit = async () => {
    if (!form.title || !form.entryFee || !form.prize || !form.totalSlots || !form.matchTime) {
      alert('সব required field পূরণ করো!');
      return;
    }
    setLoading(true);
    try {
      if (editId) {
        await updateDoc(doc(db, 'matches', editId), {
          ...form,
          entryFee: Number(form.entryFee),
          prize: Number(form.prize),
          perKill: Number(form.perKill),
          totalSlots: Number(form.totalSlots),
          prizeList: form.prizeList,
        });
        setEditId(null);
      } else {
        const allSnap = await getDocs(collection(db, 'matches'));
        const matchNumber = allSnap.size + 1;
        await addDoc(collection(db, 'matches'), {
          ...form,
          matchNumber,
          entryFee: Number(form.entryFee),
          prize: Number(form.prize),
          perKill: Number(form.perKill),
          totalSlots: Number(form.totalSlots),
          prizeList: form.prizeList,
          filledSlots: 0,
          createdAt: serverTimestamp(),
        });
      }
      setForm(EMPTY_FORM);
      setShowForm(false);
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('এই match টা delete করবে?')) return;
    await deleteDoc(doc(db, 'matches', id));
  };

  const handleEdit = (match) => {
    setForm({
      title: match.title,
      mode: match.mode,
      entryFee: String(match.entryFee),
      prize: String(match.prize),
      perKill: String(match.perKill ?? ''),
      totalSlots: String(match.totalSlots),
      matchTime: match.matchTime,
      rules: match.rules ?? '',
      status: match.status,
      map: match.map ?? 'Bermuda',
      type: match.type ?? 'Solo',
      device: match.device ?? 'Android',
      prizeList: match.prizeList?.length ? match.prizeList : DEFAULT_PRIZE_LIST,
    });
    setEditId(match.id);
    setShowForm(true);
  };

  const handleRoomUpdate = async (id) => {
    await updateDoc(doc(db, 'matches', id), {
      roomId: roomForm.roomId ?? '',
      roomPassword: roomForm.roomPassword ?? '',
    });

    try {
      const bookingSnap = await getDocs(
        query(collection(db, 'bookings'), where('matchId', '==', id))
      );

      const userIds = [...new Set(bookingSnap.docs.map(d => d.data().userId).filter(Boolean))];

      const tokens = [];
      for (const uid of userIds) {
        const userSnap = await getDoc(doc(db, 'users', uid));
        if (userSnap.exists()) {
          const token = userSnap.data()?.expoPushToken;
          if (token) tokens.push(token);
        }
      }

      if (tokens.length > 0) {
        const match = matches.find(m => m.id === id);
        await fetch('https://exp.host/--/api/v2/push/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(tokens.map(token => ({
            to:    token,
            title: '🔑 Room ID দেওয়া হয়েছে!',
            body:  `Match: ${match?.title ?? ''} | Room: ${roomForm.roomId} | Pass: ${roomForm.roomPassword}`,
            sound: 'default',
            data:  { matchId: id },
          }))),
        });
      }
    } catch (err) {
      console.warn('Notification error:', err);
    }

    setShowRoomModal(null);
    setRoomForm({});
    alert('✅ Room ID দেওয়া হয়েছে! Players কে notification পাঠানো হয়েছে।');
  };

  const updatePrizeItem = (i, field, value) => {
    const updated = [...form.prizeList];
    updated[i] = { ...updated[i], [field]: value };
    setForm({ ...form, prizeList: updated });
  };

  const addPrizeRow = () => {
    setForm({ ...form, prizeList: [...form.prizeList, { emoji: '🏅', label: '', amount: '' }] });
  };

  const removePrizeRow = (i) => {
    setForm({ ...form, prizeList: form.prizeList.filter((_, idx) => idx !== i) });
  };

  const sidebarLinks = [
    { to: '/', label: '🏠 Dashboard' },
    { to: '/matches', label: '🎯 Matches', active: true },
    { to: '/results', label: '🏆 Results' },
    { to: '/users', label: '👥 Users' },
    { to: '/deposits', label: '💰 Deposits' },
    { to: '/withdrawals', label: '⬆️ Withdrawals' },
    { to: '/settings', label: '⚙️ Settings' },
    { to: '/password-resets', label: '🔑 Password Resets' },
    { to: '/videos', label: '🎬 Videos' }
  ];

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.pageTitle}>🎯 Matches</h1>
            <p style={styles.pageSub}>Match add, edit, delete এবং Room ID দাও</p>
          </div>
          <button style={styles.addBtn}
            onClick={() => { setShowForm(!showForm); setEditId(null); setForm(EMPTY_FORM); }}>
            {showForm ? '✕ বন্ধ করো' : '➕ নতুন Match'}
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              {editId ? '✏️ Match Edit করো' : '➕ নতুন Match Add করো'}
            </h2>

            <div style={styles.sectionLabel}>📋 Basic Info</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Match Title *</label>
                <input style={styles.input} placeholder="যেমন: BR MATCH || Match No.96863"
                  value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Game Mode *</label>
                <select style={styles.input} value={form.mode}
                  onChange={e => setForm({ ...form, mode: e.target.value })}>
                  <option value="BR">BR — Battle Royale</option>
                  <option value="CS">CS — Clash Squad</option>
                  <option value="LW">LW — Lone Wolf</option>
                  <option value="PL">PL — Pro League</option>
                  <option value="CS4">CS4 — CS 4 vs 4</option>
                  <option value="SS">SS — Solo Survival</option>
                  <option value="HS">HS — Headshot Mode</option>
                  <option value="FM">FM — Free Match</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Map</label>
                <select style={styles.input} value={form.map}
                  onChange={e => setForm({ ...form, map: e.target.value })}>
                  <option value="Bermuda">Bermuda</option>
                  <option value="Purgatory">Purgatory</option>
                  <option value="Kalahari">Kalahari</option>
                  <option value="Alpine">Alpine</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Match Type</label>
                <select style={styles.input} value={form.type}
                  onChange={e => setForm({ ...form, type: e.target.value })}>
                  <option value="Solo">Solo</option>
                  <option value="Duo">Duo</option>
                  <option value="Squad">Squad</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Device</label>
                <select style={styles.input} value={form.device}
                  onChange={e => setForm({ ...form, device: e.target.value })}>
                  <option value="Android">Android</option>
                  <option value="iOS">iOS</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select style={styles.input} value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="live">Live</option>
                  <option value="ended">Ended</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Match Time *</label>
                <input style={styles.input} placeholder="যেমন: রাত ৯টা / 9:00 PM"
                  value={form.matchTime} onChange={e => setForm({ ...form, matchTime: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Slots *</label>
                <input style={styles.input} type="number" placeholder="48"
                  value={form.totalSlots} onChange={e => setForm({ ...form, totalSlots: e.target.value })} />
              </div>
            </div>

            <div style={styles.sectionLabel}>💰 Prize & Fee</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Entry Fee (৳) *</label>
                <input style={styles.input} type="number" placeholder="50"
                  value={form.entryFee} onChange={e => setForm({ ...form, entryFee: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Total Prize Pool (৳) *</label>
                <input style={styles.input} type="number" placeholder="1000"
                  value={form.prize} onChange={e => setForm({ ...form, prize: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Per Kill (৳)</label>
                <input style={styles.input} type="number" placeholder="5"
                  value={form.perKill} onChange={e => setForm({ ...form, perKill: e.target.value })} />
              </div>
            </div>

            <div style={styles.sectionLabel}>🏆 Prize List</div>
            <div style={styles.prizeListBox}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button type="button" style={styles.addRowBtn} onClick={addPrizeRow}>+ Add Row</button>
              </div>
              {form.prizeList.map((item, i) => (
                <div key={i} style={styles.prizeRow}>
                  <input
                    style={{ ...styles.input, width: 54, textAlign: 'center', flexShrink: 0 }}
                    value={item.emoji}
                    onChange={e => updatePrizeItem(i, 'emoji', e.target.value)}
                    placeholder="🥇"
                  />
                  <input
                    style={{ ...styles.input, flex: 1 }}
                    value={item.label}
                    onChange={e => updatePrizeItem(i, 'label', e.target.value)}
                    placeholder="Winner / 2nd Position..."
                  />
                  <input
                    style={{ ...styles.input, width: 130, flexShrink: 0 }}
                    value={item.amount}
                    onChange={e => updatePrizeItem(i, 'amount', e.target.value)}
                    placeholder="৳ Amount"
                  />
                  <button type="button" style={styles.removeBtn} onClick={() => removePrizeRow(i)}>✕</button>
                </div>
              ))}
            </div>

            <div style={styles.sectionLabel}>📜 Rules</div>
            <div style={styles.formGroup}>
              <textarea style={{ ...styles.input, height: 150, resize: 'vertical' }}
                placeholder="Rules লেখো..."
                value={form.rules} onChange={e => setForm({ ...form, rules: e.target.value })} />
            </div>

            <button style={{ ...styles.addBtn, opacity: loading ? 0.6 : 1, width: '100%', marginTop: 8 }}
              onClick={handleSubmit} disabled={loading}>
              {loading ? 'Save হচ্ছে...' : editId ? '✅ Update করো' : '✅ Match Add করো'}
            </button>
          </div>
        )}

        {/* Match Table */}
        <div style={styles.tableCard}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['#', 'Title', 'Mode', 'Map', 'Type', 'Entry', 'Prize', 'Per Kill', 'Slots', 'Time', 'Status', 'Room', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matches.length === 0 ? (
                <tr>
                  <td colSpan={13} style={{ ...styles.td, textAlign: 'center', color: '#555', padding: 40 }}>
                    কোনো match নেই
                  </td>
                </tr>
              ) : matches.map(m => (
                <tr key={m.id} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={{ color: '#f4c430', fontWeight: 'bold' }}>#{m.matchNumber ?? '—'}</span>
                  </td>
                  <td style={styles.td}>{m.title}</td>
                  <td style={styles.td}>
                    <span style={{
                      background: m.mode === 'BR' ? '#ff6b3522' : m.mode === 'CS' ? '#7c3aed22' : '#05966922',
                      color: m.mode === 'BR' ? '#ff6b35' : m.mode === 'CS' ? '#7c3aed' : '#059669',
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
                    }}>{m.mode}</span>
                  </td>
                  <td style={styles.td}>{m.map ?? '—'}</td>
                  <td style={styles.td}>{m.type ?? '—'}</td>
                  <td style={styles.td}>৳{m.entryFee}</td>
                  <td style={styles.td}>৳{m.prize}</td>
                  <td style={styles.td}>৳{m.perKill ?? 0}</td>
                  <td style={styles.td}>{m.filledSlots ?? 0}/{m.totalSlots}</td>
                  <td style={styles.td}>{m.matchTime}</td>
                  <td style={styles.td}>
                    <span style={{
                      background: m.status === 'open' ? '#00c89622' : '#ff444422',
                      color: m.status === 'open' ? '#00c896' : '#ff4444',
                      padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 'bold',
                    }}>{m.status}</span>
                  </td>
                  <td style={styles.td}>
                    {m.roomId ? (
                      <span style={{ color: '#00c896', fontSize: 12 }}>✅ {m.roomId}</span>
                    ) : (
                      <span style={{ color: '#888', fontSize: 12 }}>দেওয়া হয়নি</span>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={styles.detailBtn}
                        onClick={async () => {
                         setShowDetails(m);
                          const snap = await getDocs(query(collection(db, 'bookings'), where('matchId', '==', m.id)));
                           const participants = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  
                            const withPhone = await Promise.all(participants.map(async (p) => {
                             if (p.userId) {
                          const userSnap = await getDoc(doc(db, 'users', p.userId));
                            if (userSnap.exists()) {
                           return { ...p, phone: userSnap.data()?.phone ?? '—' };
                             }
                         }
                          return { ...p, phone: '—' };
                         }));
  
                     setDetailParticipants(withPhone);
                       }}>
                        👁</button>
                      <button style={styles.editBtn} onClick={() => handleEdit(m)}>✏️</button>
                      <button style={styles.roomBtn}
                        onClick={() => {
                          setShowRoomModal(m.id);
                          setRoomForm({ roomId: m.roomId ?? '', roomPassword: m.roomPassword ?? '' });
                        }}>🔑</button>
                      <button style={styles.deleteBtn} onClick={() => handleDelete(m.id)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Room Modal */}
      {showRoomModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h2 style={{ color: '#fff', marginBottom: 20 }}>🔑 Room ID দাও</h2>
            <label style={styles.label}>Room ID</label>
            <input style={styles.input} placeholder="যেমন: 8842937"
              value={roomForm.roomId ?? ''}
              onChange={e => setRoomForm({ ...roomForm, roomId: e.target.value })} />
            <label style={{ ...styles.label, marginTop: 12 }}>Room Password</label>
            <input style={styles.input} placeholder="যেমন: FF2024"
              value={roomForm.roomPassword ?? ''}
              onChange={e => setRoomForm({ ...roomForm, roomPassword: e.target.value })} />
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button style={styles.addBtn} onClick={() => handleRoomUpdate(showRoomModal)}>
                ✅ Save করো
              </button>
              <button style={styles.deleteBtn} onClick={() => setShowRoomModal(null)}>
                ✕ বন্ধ করো
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetails && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, width: 500, maxHeight: '80vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#fff', marginBottom: 4 }}>👁 Match Details</h2>
            <p style={{ color: '#f4c430', marginBottom: 4 }}>#{showDetails.matchNumber} — {showDetails.title}</p>
            <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
              {showDetails.mode} • {showDetails.matchTime} • {showDetails.map}
            </p>
            <div style={{ color: '#888', fontSize: 12, marginBottom: 10 }}>
              👥 Participants ({detailParticipants.length}/{showDetails.totalSlots})
            </div>
            {detailParticipants.length === 0 ? (
              <p style={{ color: '#555', textAlign: 'center', padding: 20 }}>কেউ join করেনি</p>
            ) : detailParticipants.map((p, i) => (
              <div key={p.id} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 0', borderBottom: '1px solid #1e1e3a',
              }}>
              <span style={{ color: '#555', width: 24 }}>{i + 1}.</span>
              <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: '600' }}>{p.gameName}</div>
              <div style={{ color: '#f4c430', fontSize: 11, marginTop: 2 }}>
            📱 0{p.phone}
             </div>
              <div style={{ color: '#555', fontSize: 11, marginTop: 1, wordBreak: 'break-all' }}>
               🆔 {p.userId ?? '—'}
              </div>
              </div>
              <span style={{
               background: '#7c3aed22', color: '#7c3aed',
               fontSize: 11, padding: '3px 8px', borderRadius: 6,
               }}>{p.joinType}</span>
              </div>
            ))}
            <button style={{ ...styles.addBtn, marginTop: 20, width: '100%' }}
              onClick={() => setShowDetails(null)}>
              ✕ বন্ধ করো
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  detailBtn: { background: '#0ea5e922', border: '1px solid #0ea5e944', color: '#0ea5e9', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub: { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#888', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent' },
  navItemActive: { color: '#f4c430', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430', background: '#f4c43011' },
  main: { marginLeft: 240, flex: 1, padding: 32 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub: { color: '#666', fontSize: 14, marginTop: 6 },
  addBtn: { background: '#f4c430', border: 'none', borderRadius: 10, color: '#000', fontSize: 14, fontWeight: 'bold', padding: '12px 20px', cursor: 'pointer' },
  formCard: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', padding: 28, marginBottom: 24 },
  formTitle: { color: '#fff', fontSize: 18, fontWeight: 'bold', marginBottom: 20, marginTop: 0 },
  sectionLabel: {
    color: '#f4c430', fontSize: 13, fontWeight: 'bold',
    letterSpacing: 1, marginBottom: 12, marginTop: 20,
    paddingBottom: 8, borderBottom: '1px solid #1e1e3a',
  },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 8 },
  formGroup: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 },
  label: { color: '#888', fontSize: 12, fontWeight: '600', letterSpacing: 0.5 },
  input: { background: '#0a0a1a', border: '1px solid #333', borderRadius: 8, color: '#fff', fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  prizeListBox: { background: '#0d0d20', borderRadius: 12, border: '1px solid #1e1e3a', padding: 16, marginBottom: 8 },
  prizeRow: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  addRowBtn: { background: '#f4c43022', border: '1px solid #f4c43066', color: '#f4c430', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: '600' },
  removeBtn: { background: '#ff444422', border: '1px solid #ff444444', color: '#ff4444', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', fontSize: 14, flexShrink: 0 },
  tableCard: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#666', fontSize: 12, fontWeight: '600', padding: '14px 16px', textAlign: 'left', borderBottom: '1px solid #1e1e3a', whiteSpace: 'nowrap' },
  td: { color: '#ccc', fontSize: 13, padding: '14px 16px', borderBottom: '1px solid #1e1e3a11' },
  tr: { transition: 'background 0.15s' },
  editBtn: { background: '#f4c43022', border: '1px solid #f4c43044', color: '#f4c430', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 },
  roomBtn: { background: '#00c89622', border: '1px solid #00c89644', color: '#00c896', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 },
  deleteBtn: { background: '#ff444422', border: '1px solid #ff444444', color: '#ff6b6b', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 14 },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#000000aa', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', padding: 32, width: 400 },
};